import { defineStore } from 'pinia'

// Define the expiration time for drafts in milliseconds (90 days)
const DRAFT_EXPIRATION_MS = 90 * 24 * 60 * 60 * 1000;

interface DirectorNoteDraft {
  content?: string
  [key: string]: unknown
}

interface ComposerStateDraft {
  [key: string]: unknown
}

interface DraftEntry {
  text: string
  director_note: DirectorNoteDraft | null
  composer_state: ComposerStateDraft | null
  timestamp: number
}

interface DraftResult {
  text: string
  director_note: DirectorNoteDraft | null
  composer_state: ComposerStateDraft | null
}

interface DraftState {
  conversationDrafts: Record<string, DraftEntry>
}

export const useDraftStore = defineStore('drafts', {
  state: (): DraftState => ({
    conversationDrafts: {},
  }),

  // Enable persistence for this entire store.
  persist: true,

  getters: {
    getValidDraftByConversationId: (state) => (conversationId: string): DraftResult => {
      const draft = state.conversationDrafts[conversationId];

      // If no draft exists, return an empty string.
      if (!draft) {
        return {
          text: '',
          director_note: null,
          composer_state: null,
        };
      }

      // Check if the draft is expired.
      const isExpired = (Date.now() - draft.timestamp) > DRAFT_EXPIRATION_MS;

      // Return the text if it's not expired, otherwise return an empty string.
      if (isExpired) {
        return {
          text: '',
          director_note: null,
          composer_state: null,
        };
      }

      if (
        typeof draft.text === 'string' &&
        draft.director_note === undefined &&
        draft.composer_state === undefined
      ) {
        return {
          text: draft.text,
          director_note: null,
          composer_state: null,
        };
      }

      return {
        text: typeof draft.text === 'string' ? draft.text : '',
        director_note: draft.director_note || null,
        composer_state: draft.composer_state || null,
      };
    },
  },

  actions: {
    saveDraft({ conversationId, text, director_note = null, composer_state = null }: {
      conversationId: string
      text: string
      director_note?: DirectorNoteDraft | null
      composer_state?: ComposerStateDraft | null
    }) {
      // If the input text is empty or just whitespace, remove the draft.
      const hasText = Boolean(text && text.trim() !== '')
      const hasDirectorNote = Boolean(director_note?.content?.trim?.())
      const hasComposerState = Boolean(composer_state && typeof composer_state === 'object')

      if (!hasText && !hasDirectorNote && !hasComposerState) {
        delete this.conversationDrafts[conversationId];
        return;
      }

      // Otherwise, update or create the draft with the new text and a fresh timestamp.
      this.conversationDrafts[conversationId] = {
        text: text,
        director_note: hasDirectorNote ? director_note : null,
        composer_state: hasComposerState ? composer_state : null,
        timestamp: Date.now(),
      };
    },

    clearExpiredDraft(conversationId: string) {
        const draft = this.conversationDrafts[conversationId];
        if (!draft) return;

        const isExpired = (Date.now() - draft.timestamp) > DRAFT_EXPIRATION_MS;
        if (isExpired) {
            delete this.conversationDrafts[conversationId];
        }
    },
  },
})
